import React from "react"
import { observer } from "mobx-react-lite"

import Icon from "@components/ui/Icon/Icon"
import TextResult from "@pages/launch-solutions/SolutionTabsContainer/components/Tabs/FactFinder/components/SourceView/components/TextResult/TextResult"
import Text from "@components/ui/Typography/Text"
import { useController } from "@store"
import Loader from "@components/ui/Loader/BarLoader"
import {
  ContentCitationData,
  SearchCitationData,
} from "@store/search/search-citations.store"
import Markdown from "@components/ui/Typography/Markdown"

import ExpertAnswer from "./ExpertAnswer"

import styles from "./AnswerViewer.module.sass"

const DEFAULT_SCALE = 90

const isViewablePDF = (citation: ContentCitationData) => {
  return (
    citation?.sourceType != null &&
    ["public", "manual"].includes(citation.sourceType.toLowerCase()) &&
    citation.sourceURL?.endsWith(".pdf")
  )
}

const skipPDFCheck = (citation: ContentCitationData) => {
  return (
    citation?.sourceType != null &&
    ["manual"].includes(citation.sourceType.toLowerCase())
  )
}

const AnswerViewer: React.FC<{
  citation: SearchCitationData
  fullscreen: boolean
  onFullScreenToggle: (show: boolean) => void
}> = observer(({ citation, fullscreen, onFullScreenToggle }) => {
  const [scale, setScale] = React.useState<number>(DEFAULT_SCALE)
  const [pdfState, setPdfState] = React.useState<{
    isPdf: boolean
    pdfUrl: string
    loadingPdf: boolean
  }>({
    isPdf: false,
    pdfUrl: "",
    loadingPdf: false,
  })

  const { dataConnectorController } = useController()

  const sourceUrl = citation?.sourceURL
  const sourcePage = citation?.answerSource?.page

  const handleScaleUp = () => {
    setScale((currScale) => (currScale < 170 ? currScale + 10 : currScale))
  }

  const handleScaleDown = () => {
    setScale((currScale) => (currScale > 20 ? currScale - 10 : currScale))
  }

  const getPdfUrl = React.useCallback(
    async (sourceURL: string, skipPDFCheck: boolean) => {
      try {
        setPdfState((prevState) => ({ ...prevState, loadingPdf: true }))

        const res = await dataConnectorController.getSignedDataURL(sourceURL)

        if (
          res.status === "FAILED" ||
          (!skipPDFCheck &&
            !(await dataConnectorController.isURLCanBeShown(res.data)))
        ) {
          throw new Error("PDF cannot be shown")
        }

        setPdfState((prevState) => ({
          ...prevState,
          isPdf: true,
          pdfUrl: `${res.data}${sourcePage ? `#page=${sourcePage}` : ""}`,
          loadingPdf: false,
        }))
      } catch (error) {
        setPdfState((prevState) => ({
          ...prevState,
          isPdf: false,
          loadingPdf: false,
        }))
      }
    },
    []
  )

  React.useEffect(() => {
    setScale(DEFAULT_SCALE)
    if (!citation?.isExpertAnswer && sourceUrl && isViewablePDF(citation)) {
      setPdfState({
        isPdf: true,
        pdfUrl: sourceUrl,
        loadingPdf: false,
      })
      getPdfUrl(sourceUrl, skipPDFCheck(citation))
    } else {
      setPdfState({ isPdf: false, pdfUrl: "", loadingPdf: false })
    }
  }, [citation, getPdfUrl])

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {pdfState.isPdf ? (
          <div className={styles.pdfContainer}>
            {pdfState.loadingPdf ? (
              <Loader size="medium" fluid />
            ) : (
              <iframe
                title={citation?.answerSource?.source}
                src={pdfState.pdfUrl}
              />
            )}
          </div>
        ) : citation?.url ? (
          <div className={styles.imageContainer}>
            <img
              style={{
                width: fullscreen ? `${scale}%` : "100%",
              }}
              src={citation?.url}
              alt={citation?.url}
            />
            <div className={styles.control}>
              <Icon
                onClick={() => onFullScreenToggle(!fullscreen)}
                className={styles.controlButton}
                name={fullscreen ? "minimize" : "maximize"}
              />
              {fullscreen && (
                <>
                  <Icon
                    onClick={handleScaleUp}
                    className={styles.controlButton}
                    name="zoom-in"
                  />
                  <Icon
                    onClick={handleScaleDown}
                    className={styles.controlButton}
                    name="zoom-out"
                  />
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            {citation?.isExpertAnswer ? (
              <ExpertAnswer details={citation} />
            ) : (
              <Text className={styles.extractedTextHeading} variant="body1">
                Extracted Text
              </Text>
            )}

            {citation?.isExpertAnswer ? (
              <Markdown className={styles.textContent}>
                {citation?.description ?? ""}
              </Markdown>
            ) : (
              <TextResult
                className={styles.textContent}
                content={citation?.description}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
})

export default AnswerViewer
